import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { baseurl } from "../config/url";
import { FiEdit2, FiTrash2, FiSearch, FiX, FiPlus } from "react-icons/fi";

const PAGE_SIZE = 10;

const safeText = (v) => (v === null || v === undefined || v === "" ? "-" : String(v));

const SubcategoryManagement = () => {
  const [subcategories, setSubcategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({ name: "", category: "", image: null, id: null });
  const isEditing = Boolean(form.id);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Search + filter + pagination
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [page, setPage] = useState(1);

  // Auth
  const getToken = () => {
    try {
      const auth = JSON.parse(localStorage.getItem("auth") || "{}");
      return auth?.token || localStorage.getItem("token") || "";
    } catch {
      return localStorage.getItem("token") || "";
    }
  };

  const token = getToken();

  const authConfig = () => ({
    headers: { Authorization: `Bearer ${token}` },
    withCredentials: true,
  });

  const multipartConfig = () => ({
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "multipart/form-data",
    },
    withCredentials: true,
  });

  const resetMessages = () => {
    setError("");
    setSuccess("");
  };

  const resetForm = () => {
    setForm({ name: "", category: "", image: null, id: null });
    resetMessages();
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${baseurl}/api/category/get-all`, authConfig());
      const list = Array.isArray(res.data) ? res.data : res.data?.categories || [];
      setCategories(list);
    } catch (err) {
      console.error("Error fetching categories:", err?.response?.data || err.message);
    }
  };

  const fetchSubcategories = async () => {
    try {
      setLoading(true);
      resetMessages();

      const res = await axios.get(`${baseurl}/api/subcategory`, authConfig());
      const list = Array.isArray(res.data) ? res.data : res.data?.subcategories || [];
      setSubcategories(list);
    } catch (err) {
      console.error("Error fetching subcategories:", err?.response?.data || err.message);
      setError(err?.response?.data?.message || "Failed to load subcategories. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchSubcategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filters
  const filtered = useMemo(() => {
    let list = [...subcategories];

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((s) => {
        const name = safeText(s?.name).toLowerCase();
        const id = safeText(s?._id).toLowerCase();
        const catName = safeText(s?.category?.name).toLowerCase();
        return name.includes(q) || id.includes(q) || catName.includes(q);
      });
    }

    if (categoryFilter !== "all") {
      list = list.filter((s) => safeText(s?.category?._id) === categoryFilter);
    }

    return list;
  }, [subcategories, search, categoryFilter]);

  // Pagination
  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(start, start + PAGE_SIZE);
  const from = totalItems === 0 ? 0 : start + 1;
  const to = Math.min(start + PAGE_SIZE, totalItems);

  useEffect(() => setPage(1), [search, categoryFilter]);
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // Create
  const createSubcategory = async () => {
    const fd = new FormData();
    fd.append("name", form.name.trim());
    fd.append("category", form.category);
    if (form.image) fd.append("image", form.image);

    await axios.post(`${baseurl}/api/subcategory`, fd, multipartConfig());
  };

  // Update
  const updateSubcategory = async () => {
    const fd = new FormData();
    fd.append("name", form.name.trim());
    fd.append("category", form.category);
    if (form.image) fd.append("image", form.image);

    await axios.put(`${baseurl}/api/subcategory/${form.id}`, fd, multipartConfig());
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    resetMessages();

    const name = form.name.trim();
    if (!name || !form.category) {
      setError("Both subcategory name and category are required.");
      return;
    }

    try {
      if (isEditing) {
        await updateSubcategory();
        setSuccess("Subcategory updated successfully!");
      } else {
        await createSubcategory();
        setSuccess("Subcategory created successfully!");
      }

      resetForm();
      await fetchSubcategories();
    } catch (err) {
      console.error("Error saving subcategory:", err?.response?.data || err.message);
      setError(err?.response?.data?.message || "Failed to save subcategory. Please try again.");
    }
  };

  const handleEdit = (subcategory) => {
    resetMessages();
    setForm({
      name: subcategory?.name || "",
      category: subcategory?.category?._id || "",
      image: null, // keep null; only replace if user selects a new file
      id: subcategory?._id || null,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteSubcategory = async (id) => {
    resetMessages();
    if (!window.confirm("Are you sure you want to delete this subcategory?")) return;

    try {
      await axios.delete(`${baseurl}/api/subcategory/${id}`, authConfig());
      setSuccess("Subcategory deleted successfully!");
      await fetchSubcategories();
    } catch (err) {
      console.error("Error deleting subcategory:", err?.response?.data || err.message);
      setError(err?.response?.data?.message || "Failed to delete subcategory. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
          <div className="text-lg font-semibold text-gray-800">Loading subcategories…</div>
          <div className="text-sm text-gray-500 mt-1">Please wait</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Subcategory Management</h1>
            <p className="text-sm text-gray-600 mt-1">Create, edit and manage subcategories.</p>
          </div>

          <div className="text-sm text-gray-600 bg-white border border-gray-200 rounded-lg px-3 py-2">
            Total: <b>{subcategories.length}</b> • Showing: <b>{filtered.length}</b>
          </div>
        </div>

        {/* Alerts */}
        {(error || success) && (
          <div className="space-y-2">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
                {error}
              </div>
            )}
            {success && (
              <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 text-sm">
                {success}
              </div>
            )}
          </div>
        )}

        {/* Create/Edit Card */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-lg font-bold text-gray-900">
                {isEditing ? "Edit Subcategory" : "Create Subcategory"}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {isEditing ? `Editing ID: ${safeText(form.id)}` : "Add a new subcategory under a category"}
              </div>
            </div>

            {isEditing && (
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800"
              >
                Cancel
              </button>
            )}
          </div>

          <form onSubmit={handleFormSubmit} encType="multipart/form-data" className="mt-4 grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-5">
              <label className="text-xs text-gray-500">Subcategory Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g., TMT Bars, LED Lights, Safety Helmets..."
                className="w-full mt-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                required
              />
            </div>

            <div className="md:col-span-4">
              <label className="text-xs text-gray-500">Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                required
              >
                <option value="">Select Category</option>
                {categories.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="text-xs text-gray-500">Image (optional)</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setForm((p) => ({ ...p, image: e.target.files?.[0] || null }))}
                className="w-full mt-1 px-3 py-2 border rounded-lg bg-white"
              />
              {form.image && (
                <div className="text-[11px] text-gray-500 mt-1 truncate">
                  Selected: {form.image.name}
                </div>
              )}
            </div>

            <div className="md:col-span-1 flex items-end">
              <button
                type="submit"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                <FiPlus />
                {isEditing ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>

        {/* Filters + Table */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          {/* Filters bar */}
          <div className="p-4 flex items-center justify-between gap-3 flex-wrap border-b">
            <div className="flex gap-3 flex-wrap items-center w-full lg:w-auto">
              <div className="relative w-full sm:w-80">
                <FiSearch className="absolute left-3 top-2.5 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name / category / ID..."
                  className="w-full pl-9 pr-9 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-700"
                    aria-label="Clear search"
                  >
                    <FiX />
                  </button>
                )}
              </div>

              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full sm:w-64 px-3 py-2 border rounded-lg bg-white text-sm"
              >
                <option value="all">All Categories</option>
                {categories.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="text-sm text-gray-600">
              Showing <b>{from}</b>–<b>{to}</b> of <b>{totalItems}</b>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Subcategory</th>
                  <th className="px-4 py-3 text-left font-semibold">Category</th>
                  <th className="px-4 py-3 text-left font-semibold">Image</th>
                  <th className="px-4 py-3 text-left font-semibold">ID</th>
                  <th className="px-4 py-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>

              <tbody>
                {pageItems.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                      No subcategories found.
                    </td>
                  </tr>
                ) : (
                  pageItems.map((s) => {
                    const imgSrc = s?.image ? `${baseurl}${s.image}` : "";
                    return (
                      <tr key={s._id} className="border-t hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-900">{safeText(s?.name)}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            Under: {safeText(s?.category?.name)}
                          </div>
                        </td>

                        <td className="px-4 py-3 text-gray-700">{safeText(s?.category?.name)}</td>

                        <td className="px-4 py-3">
                          {imgSrc ? (
                            <img
                              src={imgSrc}
                              alt={safeText(s?.name)}
                              className="w-28 h-20 object-cover rounded-lg border border-gray-200"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <div className="text-xs text-gray-400">No image</div>
                          )}
                        </td>

                        <td className="px-4 py-3 text-gray-700">
                          <div className="font-mono text-xs break-all">{safeText(s?._id)}</div>
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2 flex-wrap">
                            <button
                              onClick={() => handleEdit(s)}
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-yellow-500 text-white hover:bg-yellow-600"
                            >
                              <FiEdit2 /> Edit
                            </button>

                            <button
                              onClick={() => deleteSubcategory(s._id)}
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
                            >
                              <FiTrash2 /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalItems > 0 && (
            <div className="p-4 border-t flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm text-gray-600">
                Page <b>{page}</b> of <b>{totalPages}</b>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  Prev
                </button>

                {(() => {
                  const maxBtns = 7;
                  let startPage = Math.max(1, page - Math.floor(maxBtns / 2));
                  let endPage = Math.min(totalPages, startPage + maxBtns - 1);
                  startPage = Math.max(1, endPage - maxBtns + 1);

                  const pages = [];
                  for (let p = startPage; p <= endPage; p++) pages.push(p);

                  return pages.map((p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`px-3 py-2 rounded-lg border ${
                        p === page
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-blue-600 border-blue-200 hover:bg-blue-50"
                      }`}
                    >
                      {p}
                    </button>
                  ));
                })()}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>


      </div>
    </div>
  );
};

export default SubcategoryManagement;









































// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import { baseurl } from "../config/url";

// const SubcategoryManagement = () => {
//   const [subcategories, setSubcategories] = useState([]);
//   const [categories, setCategories] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [form, setForm] = useState({ name: "", category: "", id: null });
//   const [error, setError] = useState(""); // Track form or API errors
//   const [success, setSuccess] = useState(""); // Track successful actions

//   const token = localStorage.getItem("token"); // Retrieve token from localStorage
//   const config = {
//     headers: {
//       Authorization: `Bearer ${token}`,
//     },
//   };

//   const fetchSubcategories = async () => {
//     try {
//       const response = await axios.get(`${baseurl}/api/subcategory`, config);
//       setSubcategories(response.data);
//     } catch (error) {
//       console.error("Error fetching subcategories:", error.message);
//       setError("Failed to load subcategories. Please try again.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const fetchCategories = async () => {
//     try {
//       const response = await axios.get(`${baseurl}/api/category/get-all`, config);
//       setCategories(response.data);
//     } catch (error) {
//       console.error("Error fetching categories:", error.message);
//     }
//   };

//   useEffect(() => {
//     fetchCategories();
//     fetchSubcategories();
//   }, []);

//   const handleFormSubmit = async (e) => {
//     e.preventDefault();
//     if (!form.name.trim() || !form.category.trim()) {
//       setError("Both subcategory name and category are required.");
//       return;
//     }

//     try {
//       setError("");
//       form.id ? await updateSubcategory() : await createSubcategory();
//       setSuccess(form.id ? "Subcategory updated successfully!" : "Subcategory created successfully!");
//     } catch (error) {
//       console.error("Error saving subcategory:", error.message);
//       setError("Failed to save subcategory. Please try again.");
//     }
//   };

//   const createSubcategory = async () => {
//     const formData = new FormData();
//     formData.append("name", form.name);
//     formData.append("category", form.category);
//     if (form.image) {
//         formData.append("image", form.image);
//     }

//     try {
//         await axios.post(`${baseurl}/api/subcategory`, formData, {
//             headers: {
//                 "Content-Type": "multipart/form-data",
//                 Authorization: `Bearer ${token}`,
//             },
//         });
//         setForm({ name: "", category: "", image: null, id: null });
//         fetchSubcategories();
//     } catch (error) {
//         console.error("Error creating subcategory:", error.response?.data || error.message);
//         setError("Failed to create subcategory. Please try again.");
//     }
// };

  

//   const updateSubcategory = async () => {
//     try {
//         const formData = new FormData();
//         formData.append("name", form.name);
//         formData.append("category", form.category);
//         if (form.image) formData.append("image", form.image);

//         console.log("FormData being sent:");
//         formData.forEach((value, key) => console.log(`${key}:`, value));

//         await axios.put(`${baseurl}/api/subcategory/${form.id}`, formData, config);
//         setForm({ name: "", category: "", image: null, id: null });
//         fetchSubcategories();
//     } catch (error) {
//         console.error("Error updating subcategory:", error.message);
//     }
// };



//   const deleteSubcategory = async (id) => {
//     if (!window.confirm("Are you sure you want to delete this subcategory?")) return;

//     try {
//       await axios.delete(`${baseurl}/api/subcategory/${id}`, config);
//       setSuccess("Subcategory deleted successfully!");
//       fetchSubcategories();
//     } catch (error) {
//       console.error("Error deleting subcategory:", error.message);
//       setError("Failed to delete subcategory. Please try again.");
//     }
//   };

//   const handleEdit = (subcategory) => {
//     setForm({ name: subcategory.name, category: subcategory.category._id, id: subcategory._id });
//     setError("");
//     setSuccess("");
//   };

//   if (loading) {
//     return (
//       <div className="flex items-center justify-center h-screen bg-gray-100">
//         <p className="text-xl font-semibold text-blue-600 animate-pulse">Loading Subcategories...</p>
//       </div>
//     );
//   }

//   return (
//     <div className="min-h-screen bg-gradient-to-r from-gray-50 to-blue-50 py-10 px-4 lg:px-16">
//       <div className="relative flex flex-col">
//         <div className="flex-1 p-6 bg-white rounded-lg shadow-lg">
//           <h1 className="text-4xl font-extrabold text-center text-gray-800 mb-12">
//             Subcategory Management
//           </h1>

//           {/* Error and Success Messages */}
//           {error && <p className="text-red-500 text-center mb-4">{error}</p>}
//           {success && <p className="text-green-500 text-center mb-4">{success}</p>}

//           {/* Form Section */}
//           <div className="bg-white rounded-lg shadow-md p-6 mb-6">
//             <form
//               onSubmit={handleFormSubmit}
//               className="flex flex-wrap gap-4 items-center"
//               encType="multipart/form-data"
//             >
//               <input
//                 type="text"
//                 placeholder="Subcategory Name"
//                 value={form.name}
//                 onChange={(e) => setForm({ ...form, name: e.target.value })}
//                 className="p-3 border rounded-lg flex-1"
//                 required
//               />
//               <select
//                 value={form.category}
//                 onChange={(e) => setForm({ ...form, category: e.target.value })}
//                 className="p-3 border rounded-lg flex-1"
//                 required
//               >
//                 <option value="">Select Category</option>
//                 {categories.map((category) => (
//                   <option key={category._id} value={category._id}>
//                     {category.name}
//                   </option>
//                 ))}
//               </select>
//               <input
//                 type="file"
//                 accept="image/*"
//                 onChange={(e) => setForm({ ...form, image: e.target.files[0] })}
//                 className="p-3 border rounded-lg flex-1"
//               />
//               <button
//                 type="submit"
//                 className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600"
//               >
//                 {form.id ? "Update" : "Create"}
//               </button>
//               {form.id && (
//                 <button
//                   type="button"
//                   onClick={() => setForm({ name: "", category: "", id: null })}
//                   className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600"
//                 >
//                   Cancel
//                 </button>
//               )}
//             </form>
//           </div>

//           {/* Table Section */}
//           <div className="overflow-x-auto bg-white rounded-lg shadow-md">
//             {subcategories.length > 0 ? (
//               <table className="table-auto w-full text-sm border-collapse">
//                 <thead>
//                   <tr className="bg-gradient-to-r from-blue-400 to-blue-500 text-white">
//                     <th className="px-6 py-4 text-left font-bold">ID</th>
//                     <th className="px-6 py-4 text-left font-bold">Name</th>
//                     <th className="px-6 py-4 text-left font-bold">Category</th>
//                     <th className="px-6 py-4 text-left font-bold">Image</th>
//                     <th className="px-6 py-4 text-center font-bold">Actions</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {subcategories.map((subcategory, index) => (
//                     <tr
//                       key={index}
//                       className={`${
//                         index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
//                       } hover:bg-gray-200 transition-colors duration-300`}
//                     >
//                       <td className="px-6 py-4 text-gray-800 font-semibold">{subcategory._id}</td>
//                       <td className="px-6 py-4 text-gray-700">{subcategory.name}</td>
//                       <td className="px-6 py-4 text-gray-700">{subcategory.category.name}</td>
//                       <td className="px-6 py-4">
//                         <img
//                           src={`${baseurl}${subcategory.image}`}
//                           alt={subcategory.name}
//                           className="w-32 h-24 object-cover rounded-lg transition-transform duration-300 hover:scale-105"
//                         />
//                       </td>
//                       <td className="px-6 py-4 text-center flex justify-center gap-2">
//                         <button
//                           onClick={() => handleEdit(subcategory)}
//                           className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600"
//                         >
//                           Edit
//                         </button>
//                         <button
//                           onClick={() => deleteSubcategory(subcategory._id)}
//                           className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
//                         >
//                           Delete
//                         </button>
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             ) : (
//               <p className="px-6 py-4 text-center text-gray-600 font-medium">
//                 No subcategories found.
//               </p>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default SubcategoryManagement;
